/**
 * Feb 2026 — Top-level React ErrorBoundary.
 *
 * Before this component existed, ANY uncaught render / lifecycle error
 * anywhere in the SPA tree unmounted the whole app, leaving customers
 * with a fully blank `<div id="root">` and zero recovery. Root cause of
 * the "/book is blank in prod" incident.
 *
 * This boundary:
 *   * Renders a friendly, brand-consistent fallback (not a raw stack).
 *   * Shows a collapsible "technical details" block for QA / support
 *     so we can copy-paste the exact error + component stack out of
 *     production without asking customers to open DevTools.
 *   * Logs the full error to `console.error` so it's still visible in
 *     the browser console for developers.
 *   * Offers a "Try again" reset button (re-mounts children) and a
 *     "Reload page" hard-reset for the truly stuck cases.
 *
 * Kept as a plain class component because
 * `getDerivedStateFromError` + `componentDidCatch` are still the only
 * supported React hooks for error catching (no functional API yet, as
 * of React 19).
 */
import React from "react";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Also print to console so devs / QA can grab the stack easily.
    console.error("[app-error-boundary]", error, info);
  }

  handleReset = () => {
    this.setState({ error: null, info: null });
  };

  handleReload = () => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    const message =
      this.state.error?.message || String(this.state.error) || "Unknown error";
    const stack =
      (this.state.error?.stack || "") +
      "\n\nComponent stack:" +
      (this.state.info?.componentStack || "");

    return (
      <div
        data-testid="app-error-boundary"
        className="min-h-screen bg-white text-slate-900 flex items-center justify-center px-4 py-16"
      >
        <div className="max-w-lg w-full text-center">
          <div className="mx-auto mb-6 w-14 h-14 rounded-full bg-red-100 text-red-700 flex items-center justify-center text-2xl font-bold">
            !
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0b1f4d]">
            We&rsquo;ve hit a snag
          </h1>
          <p className="mt-3 text-slate-600 leading-relaxed">
            Something on this page didn&rsquo;t load correctly. This is on us,
            not you. Try again or reload the page &mdash; if the issue keeps
            coming back, please copy the details below and email
            <a
              href="mailto:hello@awarewatertesting.com.au"
              className="text-cyan-700 hover:underline ml-1"
            >
              hello@awarewatertesting.com.au
            </a>
            .
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button
              type="button"
              onClick={this.handleReset}
              data-testid="app-error-boundary-retry"
              className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700 hover:bg-cyan-800 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              data-testid="app-error-boundary-reload"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 hover:bg-slate-50 text-slate-800 text-sm font-semibold px-5 py-2.5 transition-colors"
            >
              Reload page
            </button>
          </div>
          <details
            className="mt-8 text-left text-xs bg-slate-50 border border-slate-200 rounded-md p-3"
            data-testid="app-error-boundary-details"
          >
            <summary className="cursor-pointer font-semibold text-slate-700">
              Technical details
            </summary>
            <div className="mt-2 font-mono text-red-700 break-words">
              {message}
            </div>
            <pre className="mt-2 whitespace-pre-wrap text-[10px] text-slate-600 leading-snug">
              {stack}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
